import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { CpdOccupation, SearchOccupationsGQL } from '@cpd-app/generated';

import { CivilianWorkExperienceModel } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class WorkExperienceService {
  private filteredOccupationTitleListSubject = new BehaviorSubject<
    CivilianWorkExperienceModel[]
  >(undefined);

  public filteredOccupationTitleList$ =
    this.filteredOccupationTitleListSubject.asObservable();

  private isFuzzyOccupationResultSubject = new BehaviorSubject<boolean>(false);
  public isFuzzyOccupationResult$ =
    this.isFuzzyOccupationResultSubject.asObservable();

  private fuzzyOccupationListSubject = new BehaviorSubject<CivilianWorkExperienceModel[]>(
    []
  );
  public fuzzyOccupationList$ =
    this.fuzzyOccupationListSubject.asObservable();

  constructor(private searchOccupationsGQL: SearchOccupationsGQL) {}

  public searchOccupationTitleList(searchTerm: string) {
    this.searchOccupationsGQL
      .fetch({ searchTerm })
      .pipe(
        take(1),
        map((result) => {
          const occupationResponse = result.data.searchOccupations;
          const searchResults = Array.isArray(occupationResponse)
            ? occupationResponse
            : (occupationResponse?.search ?? []) as CpdOccupation[];
          const fuzzyResults = Array.isArray(occupationResponse)
            ? []
            : (occupationResponse?.fuzzy ?? []) as CpdOccupation[];

          const occupationList: CivilianWorkExperienceModel[] =
            searchResults.map((o: CpdOccupation) => {
              return {
                id: o.alternateTitleId,
                occupationTitle: o.title,
                onetSocCode: o.onetCode,
                industryCode: o.industryCode + '',
                workExperienceCategoryId: null,
                numberOfMonthsOfWorkExperience: 0
              };
            });

          const fuzzyOccupationList: CivilianWorkExperienceModel[] =
            fuzzyResults.map((o: CpdOccupation) => {
              return {
                id: o.alternateTitleId,
                occupationTitle: o.title,
                onetSocCode: o.onetCode,
                industryCode: o.industryCode + '',
                workExperienceCategoryId: null,
                numberOfMonthsOfWorkExperience: 0
              };
            });

          const lowerTerm = searchTerm.trim().toLowerCase();
          const isFuzzy =
            fuzzyResults.length > 0 ||
            (occupationList.length > 0 &&
              !occupationList.some((o) =>
                o.occupationTitle?.toLowerCase().includes(lowerTerm)
              ));

          this.isFuzzyOccupationResultSubject.next(isFuzzy);
          this.filteredOccupationTitleListSubject.next(occupationList);
          this.fuzzyOccupationListSubject.next(fuzzyOccupationList);
        })
      )
      .subscribe();
  }

  public clearSearchOccupationList() {
    this.isFuzzyOccupationResultSubject.next(false);
    this.filteredOccupationTitleListSubject.next(undefined);
    this.fuzzyOccupationListSubject.next([]);
  }
}
